import { supabase } from '../../core/lib/supabase';
import type { EnabledConnectors } from '../../features/chat/types/chatTypes';

export const AGENT_DEFAULT_ENABLED_SKILL_NAMES = new Set(['docx', 'web-search']);

export interface AgentSkillRow {
  id: string;
  name: string;
  display_name: string | null;
  skills_sh_id: string | null;
  description: string | null;
  created_at: string;
  user_configurable: boolean;
}

export interface UserAgentSkillRow {
  id: string;
  user_id: string;
  skill_id: string;
  enabled: boolean;
}

export function formatAgentSkillNameFallback(name: string): string {
  if (name === 'web-search') return 'Web search';
  if (name === 'gdocs') return 'Google Docs';
  if (name === 'gsheets') return 'Google Sheets';
  if (name === 'aepd') return 'AEPD';
  if (name === 'oepm') return 'OEPM';
  if (name === 'boe') return 'BOE';
  if (name === 'tc') return 'Tribunal Constitucional';
  if (name === 'cnmc') return 'CNMC';
  return name.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getAgentSkillDisplayLabel(skill: Pick<AgentSkillRow, 'name' | 'display_name'>): string {
  const d = skill.display_name?.trim();
  if (d) return d;
  return formatAgentSkillNameFallback(skill.name);
}

export const CHAT_ACTIVATED_CONNECTOR_SKILL_NAMES = [
  'euipo-caselawsearch',
  'euipo-tmclass',
  'eurlex',
  'cendoj',
  'aepd',
  'oepm',
  'boe',
  'tc',
  'cnmc',
] as const;

export async function getAgentSkillsCatalog(): Promise<AgentSkillRow[]> {
  const { data, error } = await supabase
    .from('agent_skills')
    .select('id, name, display_name, skills_sh_id, description, created_at, user_configurable')
    .order('name');

  if (error) {
    console.error('Error fetching agent_skills:', error);
    return [];
  }

  return (data || []).map((row: Record<string, unknown>) => ({
    id: row.id as string,
    name: row.name as string,
    display_name: (row.display_name as string | null) ?? null,
    skills_sh_id: (row.skills_sh_id as string | null) ?? null,
    description: (row.description as string | null) ?? null,
    created_at: row.created_at as string,
    user_configurable: row.user_configurable !== false,
  }));
}

export async function getUserAgentSkills(): Promise<UserAgentSkillRow[]> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('user_agent_skills')
    .select('id, user_id, skill_id, enabled')
    .eq('user_id', user.id);

  if (error) {
    console.error('Error fetching user_agent_skills:', error);
    return [];
  }

  return (data || []) as UserAgentSkillRow[];
}

export async function fetchAgentSkillPrefsBundle(): Promise<{
  catalog: AgentSkillRow[];
  userRows: UserAgentSkillRow[];
}> {
  const [catalog, userRows] = await Promise.all([getAgentSkillsCatalog(), getUserAgentSkills()]);
  return { catalog, userRows };
}

export function resolveSkillToggleState(
  skill: Pick<AgentSkillRow, 'name' | 'id' | 'user_configurable'>,
  userRows: UserAgentSkillRow[]
): boolean {
  if (skill.user_configurable === false) {
    return true;
  }
  const isChatConnector = (CHAT_ACTIVATED_CONNECTOR_SKILL_NAMES as readonly string[]).includes(skill.name);
  const row = userRows.find((r) => r.skill_id === skill.id);
  if (isChatConnector) {
    return row !== undefined && row.enabled === true;
  }
  if (row !== undefined) {
    return row.enabled;
  }
  return AGENT_DEFAULT_ENABLED_SKILL_NAMES.has(skill.name);
}

export function deriveConnectorAvailabilityFromPrefs(
  catalog: AgentSkillRow[],
  userRows: UserAgentSkillRow[]
): EnabledConnectors {
  const forName = (name: string): boolean => {
    const skill = catalog.find((s) => s.name === name);
    if (!skill) return false;
    return resolveSkillToggleState(skill, userRows);
  };
  return {
    euipoCaselawsearch: forName('euipo-caselawsearch'),
    euipoTmclass: forName('euipo-tmclass'),
    eurlex: forName('eurlex'),
    cendoj: forName('cendoj'),
    aepd: forName('aepd'),
    oepm: forName('oepm'),
    boe: forName('boe'),
    tc: forName('tc'),
    cnmc: forName('cnmc'),
  };
}

const CONNECTOR_SKILL_NAME_TO_UI_KEY: Record<
  string,
  keyof EnabledConnectors
> = {
  'euipo-caselawsearch': 'euipoCaselawsearch',
  'euipo-tmclass': 'euipoTmclass',
  eurlex: 'eurlex',
  cendoj: 'cendoj',
  aepd: 'aepd',
  oepm: 'oepm',
  boe: 'boe',
  tc: 'tc',
  cnmc: 'cnmc',
};

export function buildConnectorDisplayNames(
  catalog: AgentSkillRow[]
): Partial<Record<keyof EnabledConnectors, string>> {
  const out: Partial<Record<keyof EnabledConnectors, string>> = {};
  for (const name of CHAT_ACTIVATED_CONNECTOR_SKILL_NAMES) {
    const skill = catalog.find((s) => s.name === name);
    const key = CONNECTOR_SKILL_NAME_TO_UI_KEY[name];
    if (skill && key) {
      out[key] = getAgentSkillDisplayLabel(skill);
    }
  }
  return out;
}
